import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Users, CalendarHeart, PartyPopper, ArrowRight } from 'lucide-react';
import { IMAGES } from '../data/content';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { SerapeUnderline } from '../components/Serape';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: Users, t: 'Renta Total y Semi-Privada', d: 'Desde reuniones íntimas hasta rentas completas del lugar para hasta 200 invitados.' },
  { icon: PartyPopper, t: 'Barra de Cantaritos a la Medida', d: 'Una estación dedicada de jarrito de barro y degustaciones de agave guiadas por nuestros mezcaliers.' },
  { icon: CalendarHeart, t: 'Música en Vivo Adicional', d: 'Contrata mariachi, banda o un trío para ponerle ritmo a tu celebración.' },
];

const PrivateEventsPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', event_date: '', guests: '', event_type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Por favor agrega tu nombre y correo.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/event-inquiry`, form);
      toast.success('¡Gracias! Recibimos tu solicitud — te contactaremos pronto.');
      setForm({ name: '', email: '', phone: '', event_date: '', guests: '', event_type: '', message: '' });
    } catch (err) {
      toast.error('Algo salió mal. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full bg-brand-night border border-brand-line px-4 py-3 text-brand-cream placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-gold transition-colors';

  return (
    <div data-testid="private-events-page" className="bg-brand-night">
      <PageHero
        testid="events-hero"
        overline="Celebra con Nosotros"
        title="Eventos Privados"
        subtitle="Cumpleaños, eventos corporativos, quinceañeras y bodas — diseñados alrededor de la experiencia de los cantaritos en el corazón de Dallas."
        image={IMAGES.interior}
      />

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.1}>
              <div data-testid={`event-feature-${i}`} className="bg-brand-coal border border-brand-line p-8 h-full hover:border-brand-gold transition-colors">
                <f.icon className="text-brand-gold mb-4" size={26} />
                <h3 className="font-display font-semibold text-xl text-brand-cream">{f.t}</h3>
                <p className="mt-3 text-sm text-brand-cream/70 leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Inquiry form */}
      <section className="relative bg-brand-coal overflow-hidden">
        <div className="tile-greca h-9 w-full opacity-50" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <Reveal>
            <p className="uppercase text-sm tracking-[0.3em] font-semibold text-brand-gold mb-5 flex items-center gap-2">
              <Sparkles size={16} /> Comienza a Planear
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight leading-tight text-brand-cream">
              Cuéntanos sobre tu fiesta.
            </h2>
            <SerapeUnderline className="mt-6" />
            <p className="mt-6 text-brand-cream/70 leading-relaxed max-w-md">
              Comparte algunos detalles y nuestro equipo de eventos creará una propuesta — menú, cantaritos, música y todo. Normalmente respondemos en un día hábil.
            </p>
            <img src={IMAGES.cantarito3} alt="Cantarito" className="mt-10 w-full h-64 object-cover hidden lg:block border border-brand-line" />
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="event-inquiry-form" className="bg-brand-coal border border-brand-line p-8 md:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Nombre *</label>
                  <input data-testid="event-name" value={form.name} onChange={update('name')} className={inputCls} placeholder="Tu nombre" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Correo *</label>
                  <input data-testid="event-email" type="email" value={form.email} onChange={update('email')} className={inputCls} placeholder="tu@correo.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Teléfono</label>
                  <input data-testid="event-phone" value={form.phone} onChange={update('phone')} className={inputCls} placeholder="(214) 555-0000" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Fecha Preferida</label>
                  <input data-testid="event-date" type="date" value={form.event_date} onChange={update('event_date')} className={inputCls} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Invitados</label>
                  <input data-testid="event-guests" value={form.guests} onChange={update('guests')} className={inputCls} placeholder="ej. 40" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Tipo de Evento</label>
                  <input data-testid="event-type" value={form.event_type} onChange={update('event_type')} className={inputCls} placeholder="Cumpleaños, corporativo…" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Cuéntanos más</label>
                <textarea data-testid="event-message" value={form.message} onChange={update('message')} rows={4} className={inputCls} placeholder="Ambiente, preferencias de comida, música…" />
              </div>
              <button type="submit" disabled={loading} data-testid="event-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-terracotta text-white px-7 py-4 text-sm uppercase tracking-widest font-medium hover:bg-brand-terracottaDark transition-colors disabled:opacity-60">
                {loading ? 'Enviando…' : <>Enviar Solicitud <ArrowRight size={16} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default PrivateEventsPage;
